import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Loader2, Save, Image as ImageIcon, Upload, Trash2, Palette,
  Heart, Users, Award, Briefcase, Shield, Star, TrendingUp, Smile, Globe, Sparkles, Clock, Handshake,
  Plus, X as XIcon, ExternalLink,
} from 'lucide-react';

import { API, API_BASE as PUBLIC_BASE } from '../utils/apiBase';

const ICON_OPTIONS = [
  { value: 'heart', Icon: Heart },
  { value: 'users', Icon: Users },
  { value: 'award', Icon: Award },
  { value: 'briefcase', Icon: Briefcase },
  { value: 'shield', Icon: Shield },
  { value: 'star', Icon: Star },
  { value: 'trending-up', Icon: TrendingUp },
  { value: 'smile', Icon: Smile },
  { value: 'globe', Icon: Globe },
  { value: 'sparkles', Icon: Sparkles },
  { value: 'clock', Icon: Clock },
  { value: 'handshake', Icon: Handshake },
];

export default function BrandingTab({ api }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [heroBust, setHeroBust] = useState(0);
  const [logoBust, setLogoBust] = useState(0);
  const [uploading, setUploading] = useState({ hero: false, logo: false });
  const heroInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/settings/branding');
      setData(d);
      setHeroBust(Date.now());
      setLogoBust(Date.now());
    } catch {
      toast.error('Failed to load branding');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) {
    return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0] mx-auto" /></div>;
  }

  const setField = (k, v) => setData(p => ({ ...p, [k]: v }));
  const setColor = (k, v) => setData(p => ({ ...p, colors: { ...(p.colors || {}), [k]: v } }));
  const setVp = (idx, k, v) => setData(p => ({
    ...p,
    value_props: (p.value_props || []).map((vp, i) => i === idx ? { ...vp, [k]: v } : vp),
  }));
  const addVp = () => setData(p => ({
    ...p,
    value_props: [...(p.value_props || []), { id: `vp-${Date.now()}`, icon: 'heart', title: '', body: '' }].slice(0, 6),
  }));
  const removeVp = (idx) => setData(p => ({
    ...p,
    value_props: (p.value_props || []).filter((_, i) => i !== idx),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero_title: data.hero_title || '',
        hero_subtitle: data.hero_subtitle || '',
        footer_text: data.footer_text || '',
        colors: data.colors || {},
        value_props: (data.value_props || []).filter(vp => (vp.title || '').trim() || (vp.body || '').trim()),
      };
      const { data: d } = await api.put('/settings/branding', payload);
      setData(d);
      toast.success('Branding saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Image must be under 3 MB'); return; }
    setUploading(p => ({ ...p, [kind]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const path = kind === 'hero' ? '/settings/branding/hero-image' : '/settings/branding/logo';
      await api.post(path, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${kind === 'hero' ? 'Hero photo' : 'Logo'} uploaded`);
      if (kind === 'hero') setHeroBust(Date.now()); else setLogoBust(Date.now());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(p => ({ ...p, [kind]: false }));
  };

  const deleteImage = async (kind) => {
    if (!window.confirm(`Remove ${kind === 'hero' ? 'hero photo' : 'logo'}?`)) return;
    try {
      const path = kind === 'hero' ? '/settings/branding/hero-image' : '/settings/branding/logo';
      await api.delete(path);
      toast.success('Removed');
      fetchData();
    } catch { toast.error('Failed to remove'); }
  };

  const heroUrl = data.has_hero_image ? `${PUBLIC_BASE}/api/public/branding/hero-image?t=${heroBust}` : null;
  const logoUrl = data.has_logo ? `${PUBLIC_BASE}/api/public/branding/logo?t=${logoBust}` : null;
  const careersUrl = `${PUBLIC_BASE}/careers`;

  return (
    <div className="space-y-5" data-testid="branding-tab">
      {/* Public link banner */}
      <Card className="border-[#9B7ED8] bg-gradient-to-r from-[#EDE7F6] to-[#F8F7FC] shadow-sm">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#5A4D6E] uppercase tracking-wide">Live preview</p>
            <a href={careersUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-mono text-[#6B3FA0] hover:underline break-all" data-testid="branding-public-link">
              {careersUrl}
            </a>
          </div>
          <Button size="sm" variant="outline" onClick={() => window.open(careersUrl, '_blank')} className="border-[#9B7ED8] text-[#6B3FA0] gap-1.5" data-testid="branding-open-careers">
            <ExternalLink className="h-3.5 w-3.5"/>Open
          </Button>
        </CardContent>
      </Card>

      <ImageCard
        kind="hero"
        title="Hero photo"
        helpText="Replaces the purple gradient at the top of /careers. Use a high-quality landscape photo (1600×900+ recommended). JPG/PNG/WebP, under 3 MB."
        imageUrl={heroUrl}
        uploading={uploading.hero}
        inputRef={heroInputRef}
        onPick={(f) => uploadImage('hero', f)}
        onDelete={() => deleteImage('hero')}
        aspectClass="aspect-[16/6]"
      />

      <ImageCard
        kind="logo"
        title="Company logo"
        helpText="Shows in the header of /careers. Use a transparent PNG or square logo. Max 3 MB."
        imageUrl={logoUrl}
        uploading={uploading.logo}
        inputRef={logoInputRef}
        onPick={(f) => uploadImage('logo', f)}
        onDelete={() => deleteImage('logo')}
        aspectClass="aspect-square max-w-[160px]"
      />

      {/* Colors */}
      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
            <Palette className="h-4 w-4 text-[#6B3FA0]"/>Brand colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#8E85A0]">
            Drives the public Career Portal hero gradient, buttons, accents, and badges.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: 'primary', label: 'Primary', hint: 'Main buttons, links, hero gradient' },
              { key: 'accent', label: 'Accent', hint: 'Borders, hover states, lighter hero' },
              { key: 'dark', label: 'Dark', hint: 'Headlines, hero gradient end' },
            ].map(c => (
              <ColorPicker
                key={c.key}
                label={c.label}
                hint={c.hint}
                value={data.colors?.[c.key] || ''}
                onChange={(v) => setColor(c.key, v)}
                testid={`brand-color-${c.key}`}
              />
            ))}
          </div>
          <PreviewSwatch colors={data.colors} />
        </CardContent>
      </Card>

      {/* Headlines */}
      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#2D1B4E]">Headlines &amp; copy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Hero title</Label>
            <Input value={data.hero_title || ''} onChange={e => setField('hero_title', e.target.value)} className="border-[#DED8EB]" data-testid="brand-hero-title" />
          </div>
          <div>
            <Label className="text-xs">Hero subtitle</Label>
            <Textarea rows={3} value={data.hero_subtitle || ''} onChange={e => setField('hero_subtitle', e.target.value)} className="border-[#DED8EB] text-sm" data-testid="brand-hero-subtitle" />
          </div>
          <div>
            <Label className="text-xs">Footer text</Label>
            <Input value={data.footer_text || ''} onChange={e => setField('footer_text', e.target.value)} className="border-[#DED8EB]" data-testid="brand-footer-text" />
          </div>
        </CardContent>
      </Card>

      {/* Value props */}
      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-[#2D1B4E]">"Why work here?" cards</CardTitle>
          <Button size="sm" variant="outline" onClick={addVp} disabled={(data.value_props || []).length >= 6} className="border-[#9B7ED8] text-[#6B3FA0] h-7 text-xs gap-1" data-testid="brand-add-vp">
            <Plus className="h-3 w-3"/>Add card
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#8E85A0]">Up to 6 cards shown above the listings. Empty cards are skipped on save.</p>
          {(data.value_props || []).length === 0 && <p className="text-xs text-[#8E85A0] py-2 text-center">No cards. Add one above.</p>}
          {(data.value_props || []).map((vp, i) => (
            <div key={vp.id || i} className="border border-[#DED8EB] rounded-lg p-3 space-y-2 bg-[#FBF9FE]" data-testid={`brand-vp-${i}`}>
              <div className="flex items-center gap-2">
                <IconPicker value={vp.icon} onChange={(v) => setVp(i, 'icon', v)} />
                <Input value={vp.title || ''} onChange={e => setVp(i, 'title', e.target.value)} placeholder="e.g., Mission-driven" className="border-[#DED8EB] flex-1" data-testid={`brand-vp-title-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => removeVp(i)} className="h-8 w-8 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`brand-vp-remove-${i}`}>
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Textarea rows={2} value={vp.body || ''} onChange={e => setVp(i, 'body', e.target.value)} placeholder="Short description..." className="border-[#DED8EB] text-sm" data-testid={`brand-vp-body-${i}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end pt-2 border-t border-[#DED8EB]">
        <Button onClick={save} disabled={saving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="branding-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4"/>}
          {saving ? 'Saving...' : 'Save branding'}
        </Button>
      </div>
    </div>
  );
}

function ImageCard({ kind, title, helpText, imageUrl, uploading, inputRef, onPick, onDelete, aspectClass }) {
  return (
    <Card className="border-[#DED8EB] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-[#6B3FA0]"/>{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-[#8E85A0]">{helpText}</p>
        <div className={`${aspectClass} rounded-lg border-2 border-dashed border-[#DED8EB] bg-[#F7F4EF] flex items-center justify-center overflow-hidden relative`} data-testid={`branding-${kind}-preview`}>
          {imageUrl ? (
            <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="text-center px-4">
              <ImageIcon className="h-8 w-8 mx-auto text-[#9B7ED8] opacity-60" />
              <p className="text-xs text-[#8E85A0] mt-1">No {kind} uploaded</p>
            </div>
          )}
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          ref={inputRef}
          onChange={(e) => onPick(e.target.files?.[0])}
          className="hidden"
          data-testid={`branding-${kind}-input`}
        />
        <div className="flex gap-2">
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1.5" data-testid={`branding-${kind}-upload-btn`}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? 'Uploading...' : imageUrl ? 'Replace' : 'Upload'}
          </Button>
          {imageUrl && (
            <Button onClick={onDelete} variant="outline" className="border-red-300 text-[#D9534F] gap-1.5" data-testid={`branding-${kind}-remove-btn`}>
              <Trash2 className="h-3.5 w-3.5"/>Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ColorPicker({ label, hint, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2 border border-[#DED8EB] rounded-md p-2 bg-white">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-9 rounded cursor-pointer border-0 p-0 bg-transparent"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="border-0 shadow-none px-1 font-mono text-sm uppercase flex-1 focus-visible:ring-0"
          maxLength={7}
          data-testid={`${testid}-hex`}
        />
      </div>
      <p className="text-[10px] text-[#8E85A0]">{hint}</p>
    </div>
  );
}

function PreviewSwatch({ colors }) {
  const c = colors || {};
  const grad = `linear-gradient(135deg, ${c.dark || '#2D1B4E'}, ${c.primary || '#6B3FA0'})`;
  return (
    <div className="rounded-lg overflow-hidden border border-[#DED8EB]" data-testid="branding-preview-swatch">
      <div style={{ background: grad }} className="px-4 py-6 text-white">
        <p className="text-xs uppercase opacity-80">Live preview</p>
        <p className="text-xl font-extrabold">Build a meaningful career.</p>
        <div className="mt-2 flex gap-2">
          <button style={{ background: c.primary || '#6B3FA0' }} className="text-xs font-bold px-3 py-1.5 rounded-md text-white">Primary button</button>
          <Badge style={{ background: c.accent || '#9B7ED8', color: '#fff' }}>Accent badge</Badge>
        </div>
      </div>
    </div>
  );
}

function IconPicker({ value, onChange }) {
  return (
    <select
      value={value || 'heart'}
      onChange={(e) => onChange(e.target.value)}
      className="border border-[#DED8EB] rounded-md px-2 py-2 text-sm bg-white"
      data-testid="brand-vp-icon"
    >
      {ICON_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.value}</option>
      ))}
    </select>
  );
}
